import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { ROUTES } from 'features/routes';
import { FlexContainer } from 'features/common/components/FlexContainer';
import { useAudienceData } from './useAudienceData';
import { DropdownInput } from '../DropdownInput';
import { UploadCohort } from 'features/Marketing/ViewEmailCampaigns/components/UploadCohort';
import { InputLabel } from 'features/common/components/Form/InputLabel';
import { calculateTotalEmails, changeValue, controlVisibility, getFilterName } from '../../utils';
import { TargetAudienceProps } from './types';

import {
	FormText,
	ListContainer,
	Option,
	CheckboxButton,
	OptionContent,
	OptionTitle,
	OptionMeta,
	OptionBadge,
} from '../../CampaignForm.styles';
import { DateTime } from 'luxon';

export const TargetAudience = ({ label, isPunchup = false }: TargetAudienceProps & { isPunchup?: boolean }) => {
	const { t } = useTranslation();
	const firstRender = useRef(true);
	const { pathname } = useLocation();
	const isNewCampaign = pathname === ROUTES.CREATE_CAMPAIGN_PATH;
	const { cohortsData, eventsData, search, setSearch, totalPerPage, setPerPage } = useAudienceData();
	const { watch, setValue } = useFormContext();
	const cohortIds: number[] = watch('cohorts');
	const eventIds: number[] = watch('events');
	const allEventsInit: boolean = watch('allEvents');
	const allCohortsInit: boolean = watch('allCohorts');
	const [selectedEvents, setEvents] = useState(eventIds);
	const [selectedCohorts, setCohorts] = useState(cohortIds);
	const [allEvents, setAllEvents] = useState<boolean>(false);
	const [allCohorts, setAllCohorts] = useState<boolean>(false);
	const selectedCt = (cohortIds?.length || 0) + eventIds?.length;
	const updateValues = async () => {
		setValue('events', selectedEvents);
		setValue('cohorts', selectedCohorts);
		setValue('allEvents', allEvents);
		setValue('allCohorts', allCohorts);
		calculateTotalEmails({
			allCohorts,
			allEvents,
			cohortsData,
			eventsData,
			cohortIds: selectedCohorts,
			eventIds: selectedEvents,
			setValue,
		});
	};

	useEffect(() => {
		setAllEvents(allEventsInit);
		setAllCohorts(allCohortsInit);
		setEvents(allEventsInit ? [] : eventIds);
		setCohorts(allCohortsInit ? [] : cohortIds);
	}, [allEventsInit, allCohortsInit, eventIds, cohortIds]);

	useEffect(() => {
		if (firstRender.current && cohortsData && eventsData && isNewCampaign) {
			calculateTotalEmails({
				allCohorts,
				allEvents,
				cohortsData,
				eventsData,
				cohortIds: selectedCohorts,
				eventIds: selectedEvents,
				setValue,
			});
			firstRender.current = false;
		}
	}, [cohortsData, eventsData, allCohorts, allEvents, selectedCohorts, selectedEvents, isNewCampaign, setValue]);

	return (
		<FlexContainer flexDirection="column" gap="16px">
			<FormText isPunchup={isPunchup}>{label}</FormText>
			<FlexContainer flexDirection="column" gap="8px">
				<InputLabel
					label={t('marketing-campaignForm-audienceInfo')}
					htmlFor={label}
					color={isPunchup ? '#333' : undefined}
				/>
				<DropdownInput
					filterName={getFilterName({ allCohorts, allEvents, t, selectedCt })}
					// Gate View All on mode/search only: the collapsed events list is page-capped (4),
					// so its length can't reveal whether more exist — don't disable View All from it (PUN-4993).
					hideViewAll={totalPerPage === -1 || !!search}
					id={label}
					setPerPage={setPerPage}
					setSearch={setSearch}
					updateValues={updateValues}
					validationName="audience"
					isPunchup={isPunchup}
				>
					<ListContainer isPunchup={isPunchup}>
						<Option isPunchup={isPunchup}>
							<CheckboxButton
								type="checkbox"
								checked={allCohorts}
								value={t('marketing-campaignForm-allCohorts')}
								onChange={() => {
									setAllCohorts((prev) => !prev);
									setCohorts([]);
								}}
							/>
							<OptionContent>
								<OptionTitle isPunchup={isPunchup}>{t('marketing-campaignForm-allCohorts')}</OptionTitle>
							</OptionContent>
						</Option>
						<Option isPunchup={isPunchup}>
							<CheckboxButton
								type="checkbox"
								checked={allEvents}
								value={t('marketing-campaignForm-allEvents')}
								onChange={() => {
									setAllEvents((prev) => !prev);
									setEvents([]);
								}}
							/>
							<OptionContent>
								<OptionTitle isPunchup={isPunchup}>{t('marketing-campaignForm-allEvents')}</OptionTitle>
							</OptionContent>
						</Option>
						{cohortsData?.map(({ iD, name, emails, createdAt }, i) => {
							return (
								controlVisibility({ name, i, search, totalPerPage }) && (
									<Option key={iD} isPunchup={isPunchup}>
										<CheckboxButton
											type="checkbox"
											checked={selectedCohorts?.includes(iD)}
											name={name}
											value={name}
											onChange={() => changeValue(setCohorts, iD, setAllCohorts)}
										/>
										<OptionContent>
											<OptionTitle isPunchup={isPunchup}>
												<OptionBadge isPunchup={isPunchup} type="cohort">
													Cohort
												</OptionBadge>
												{name}
											</OptionTitle>
											<OptionMeta isPunchup={isPunchup}>
												{emails?.length || 0} email{emails?.length !== 1 ? 's' : ''}
												{createdAt && ` • Created ${DateTime.fromISO(createdAt).toRelative()}`}
											</OptionMeta>
										</OptionContent>
									</Option>
								)
							);
						})}
						{eventsData?.map(({ id, name, startDate, timeZone }) => (
							<Option key={id} isPunchup={isPunchup}>
								<CheckboxButton
									type="checkbox"
									checked={selectedEvents?.includes(id)}
									name={name}
									value={name}
									onChange={() => changeValue(setEvents, id, setAllEvents)}
								/>
								<OptionContent>
									<OptionTitle isPunchup={isPunchup}>
										<OptionBadge isPunchup={isPunchup} type="event">
											Event
										</OptionBadge>
										{name}
									</OptionTitle>
									<OptionMeta isPunchup={isPunchup}>
										{DateTime.fromISO(startDate).setZone(timeZone).toFormat('EEEE, MMMM d, yyyy • h:mm a ZZZZ')}
									</OptionMeta>
								</OptionContent>
							</Option>
						))}
					</ListContainer>
				</DropdownInput>
			</FlexContainer>
			<UploadCohort isPunchup={isPunchup} />
		</FlexContainer>
	);
};
